import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../axios';
import { toast } from 'react-hot-toast';
import type { Blog, BlogFormData, BlogFilters } from '../types';

// Fetch all blogs with filters
export function useBlogs(filters?: BlogFilters) {
  return useQuery({
    queryKey: ['blogs', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const { data } = await api.get(`/blogs?${params.toString()}`);
      return data.blogs as Blog[];
    },
  });
}

// Fetch single blog by ID
export function useBlog(id: string | undefined) {
  return useQuery({
    queryKey: ['blog', id],
    queryFn: async () => {
      if (!id) throw new Error('Blog ID is required');
      const { data } = await api.get(`/blogs/${id}`);
      return data as Blog;
    },
    enabled: !!id,
  });
}

// Create blog
export function useCreateBlog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BlogFormData) => {
      const { data: response } = await api.post('/blogs', data);
      return response as Blog;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blogs'] });
      toast.success('Blog created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create blog');
    },
  });
}

// Update blog
export function useUpdateBlog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: BlogFormData }) => {
      const { data: response } = await api.put(`/blogs/${id}`, data);
      return response as Blog;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['blogs'] });
      queryClient.invalidateQueries({ queryKey: ['blog', variables.id] });
      toast.success('Blog updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update blog');
    },
  });
}

// Delete blog
export function useDeleteBlog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/blogs/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blogs'] });
      toast.success('Blog deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete blog');
    },
  });
}

// Toggle publish status
export function useTogglePublish() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.patch(`/blogs/${id}/publish`);
      return data as Blog;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['blogs'] });
      queryClient.invalidateQueries({ queryKey: ['blog', id] });
      toast.success('Blog status updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update status');
    },
  });
}

// Upload image
export function useUploadBlogImage() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('image', file);

      const { data } = await api.post('/blogs/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data as { url: string; filename: string };
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to upload image');
    },
  });
}
